import React, { useEffect, useState } from 'react';
import { MapPin, Store, Utensils, CreditCard, Banknote, Smartphone, Loader2, CheckCircle2, Navigation, AlertCircle } from 'lucide-react';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';
import { DeliveryType, Restaurant, RestaurantSettings } from '../../lib/types';
import { cn } from '../../lib/utils';
interface CheckoutFormProps {
  restaurant: Restaurant | null;
  settings: RestaurantSettings | null;
  onSubmit: (data: CheckoutFormData) => void;
  isLoading: boolean;
}
export interface CheckoutFormData {
  customer_name: string;
  customer_phone: string;
  delivery_type: DeliveryType;
  payment_method: string;
  delivery_address: string;
  maps_url: string;
  delivery_lat: number | null;
  delivery_lng: number | null;
  table_id: string;
  notes: string;
}
export function CheckoutForm({
  restaurant,
  settings,
  onSubmit,
  isLoading
}: CheckoutFormProps) {
  const [formData, setFormData] = useState<CheckoutFormData>({
    customer_name: '',
    customer_phone: '',
    delivery_type: 'pickup',
    payment_method: 'sinpe',
    delivery_address: '',
    maps_url: '',
    delivery_lat: null,
    delivery_lng: null,
    table_id: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CheckoutFormData, string>>>({});
  const [geoStatus, setGeoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [geoError, setGeoError] = useState('');
  // Initialize default delivery type based on settings
  useEffect(() => {
    if (settings) {
      if (settings.accept_delivery) setFormData(prev => ({
        ...prev,
        delivery_type: 'delivery'
      }));else if (settings.accept_pickup) setFormData(prev => ({
        ...prev,
        delivery_type: 'pickup'
      }));
    }
  }, [settings]);
  const validate = () => {
    const newErrors: Partial<Record<keyof CheckoutFormData, string>> = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'El nombre es requerido';
    if (!formData.customer_phone.trim()) newErrors.customer_phone = 'El teléfono es requerido';else if (formData.customer_phone.replace(/\D/g, '').length < 8) {
      newErrors.customer_phone = 'Ingresa un número válido de 8 dígitos';
    }
    if (formData.delivery_type === 'delivery') {
      if (!formData.delivery_address.trim()) newErrors.delivery_address = 'La dirección es requerida para delivery';
    }
    if (formData.delivery_type === 'dine_in') {
      if (!formData.table_id.trim()) newErrors.table_id = 'Indica tu número de mesa';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };
  const handleChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('error');
      setGeoError('Tu navegador no soporta geolocalización');
      return;
    }
    setGeoStatus('loading');
    setGeoError('');
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      setFormData(prev => ({
        ...prev,
        delivery_lat: latitude,
        delivery_lng: longitude,
        maps_url: `https://maps.google.com/?q=${latitude},${longitude}`
      }));
      setGeoStatus('success');
    }, error => {
      setGeoStatus('error');
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGeoError('Permiso denegado. Por favor, permite el acceso a tu ubicación.');
          break;
        case error.POSITION_UNAVAILABLE:
          setGeoError('La información de ubicación no está disponible.');
          break;
        case error.TIMEOUT:
          setGeoError('Se agotó el tiempo de espera para obtener la ubicación.');
          break;
        default:
          setGeoError('Ocurrió un error desconocido al obtener la ubicación.');
          break;
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  // Determine available payment methods from restaurant
  const showSinpe = restaurant?.accepts_sinpe ?? true;
  const showCash = restaurant?.accepts_cash ?? true;
  const showCard = restaurant?.accepts_card ?? true;
  return <form onSubmit={handleSubmit} className="space-y-8" data-id="element-100">
      {/* Contact Info */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-101">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-102">Tus Datos</h3>
        <div className="space-y-4" data-id="element-103">
          <Input label="Nombre completo" placeholder="Ej. Juan Pérez" value={formData.customer_name} onChange={e => handleChange('customer_name', e.target.value)} error={errors.customer_name} required data-id="element-104" />
          <Input label="Teléfono (WhatsApp)" placeholder="Ej. 8888 8888" type="tel" value={formData.customer_phone} onChange={e => handleChange('customer_phone', e.target.value)} error={errors.customer_phone} required data-id="element-105" />
        </div>
      </section>

      {/* Delivery Type */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-106">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-107">
          Método de Entrega
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-108">
          {settings?.accept_delivery && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'delivery' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-109">
              <input type="radio" name="delivery_type" value="delivery" checked={formData.delivery_type === 'delivery'} onChange={() => handleChange('delivery_type', 'delivery')} className="sr-only" data-id="element-110" />
              <MapPin className="w-6 h-6 mb-2" data-id="element-111" />
              <span className="font-semibold" data-id="element-112">Delivery</span>
            </label>}

          {settings?.accept_pickup && <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'pickup' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-113">
              <input type="radio" name="delivery_type" value="pickup" checked={formData.delivery_type === 'pickup'} onChange={() => handleChange('delivery_type', 'pickup')} className="sr-only" data-id="element-114" />
              <Store className="w-6 h-6 mb-2" data-id="element-115" />
              <span className="font-semibold" data-id="element-116">Para llevar</span>
            </label>}

          <label className={cn('flex flex-col items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.delivery_type === 'dine_in' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-117">
            <input type="radio" name="delivery_type" value="dine_in" checked={formData.delivery_type === 'dine_in'} onChange={() => handleChange('delivery_type', 'dine_in')} className="sr-only" data-id="element-118" />
            <Utensils className="w-6 h-6 mb-2" data-id="element-119" />
            <span className="font-semibold" data-id="element-120">Comer aquí</span>
          </label>
        </div>

        {/* Conditional Fields based on Delivery Type */}
        <div className="mt-6 space-y-4" data-id="element-121">
          {formData.delivery_type === 'delivery' && <>
              <Textarea label="Dirección exacta" placeholder="Provincia, Cantón, Distrito, señas exactas..." value={formData.delivery_address} onChange={e => handleChange('delivery_address', e.target.value)} error={errors.delivery_address} required data-id="element-122" />

              <div className="space-y-2" data-id="element-123">
                <label className="block text-sm font-medium text-gray-700" data-id="element-124">
                  Ubicación GPS (Opcional pero recomendado)
                </label>

                <button type="button" onClick={handleGeolocation} disabled={geoStatus === 'loading'} className={cn('w-full h-12 flex items-center justify-center rounded-xl border-2 border-dashed font-medium transition-all disabled:opacity-70', geoStatus === 'idle' && 'border-gray-200 text-gray-600 hover:border-brand-amber hover:text-brand-amber bg-gray-50', geoStatus === 'loading' && 'border-brand-amber text-brand-amber bg-amber-50/50', geoStatus === 'success' && 'border-emerald-300 text-emerald-700 bg-emerald-50', geoStatus === 'error' && 'border-red-300 text-red-600 bg-red-50')} data-id="element-125">
                  {geoStatus === 'idle' && <>
                      <Navigation className="w-5 h-5 mr-2" data-id="element-126" />
                      Usar mi ubicación actual
                    </>}
                  {geoStatus === 'loading' && <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-127" />
                      Obteniendo ubicación...
                    </>}
                  {geoStatus === 'success' && <>
                      <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-128" />
                      Ubicación capturada
                    </>}
                  {geoStatus === 'error' && <>
                      <AlertCircle className="w-5 h-5 mr-2" data-id="element-129" />
                      Reintentar ubicación
                    </>}
                </button>

                {geoStatus === 'success' && <div className="flex items-center justify-between px-1" data-id="element-130">
                    <a href={formData.maps_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline flex items-center" data-id="element-131">
                      📍 Ver en el mapa
                    </a>
                    <button type="button" onClick={handleGeolocation} className="text-xs text-gray-500 hover:text-gray-900 underline" data-id="element-132">
                      Actualizar
                    </button>
                  </div>}

                {geoStatus === 'error' && <p className="text-xs text-red-500 px-1" data-id="element-133">{geoError}</p>}
              </div>
            </>}

          {formData.delivery_type === 'dine_in' && <Input label="Número o nombre de mesa" placeholder="Ej. Mesa 4, Barra, etc." value={formData.table_id} onChange={e => handleChange('table_id', e.target.value)} error={errors.table_id} required data-id="element-134" />}
        </div>
      </section>

      {/* Payment Method */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-135">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-136">Método de Pago</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-137">
          {showSinpe && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'sinpe' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-138">
              <input type="radio" name="payment_method" value="sinpe" checked={formData.payment_method === 'sinpe'} onChange={() => handleChange('payment_method', 'sinpe')} className="sr-only" data-id="element-139" />
              <Smartphone className="w-5 h-5 mr-3" data-id="element-140" />
              <span className="font-semibold" data-id="element-141">SINPE Móvil</span>
            </label>}

          {showCash && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'cash' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-142">
              <input type="radio" name="payment_method" value="cash" checked={formData.payment_method === 'cash'} onChange={() => handleChange('payment_method', 'cash')} className="sr-only" data-id="element-143" />
              <Banknote className="w-5 h-5 mr-3" data-id="element-144" />
              <span className="font-semibold" data-id="element-145">Efectivo</span>
            </label>}

          {showCard && <label className={cn('flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all', formData.payment_method === 'card' ? 'border-brand-amber bg-amber-50/50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-146">
              <input type="radio" name="payment_method" value="card" checked={formData.payment_method === 'card'} onChange={() => handleChange('payment_method', 'card')} className="sr-only" data-id="element-147" />
              <CreditCard className="w-5 h-5 mr-3" data-id="element-148" />
              <span className="font-semibold" data-id="element-149">Tarjeta (Datáfono)</span>
            </label>}
        </div>
      </section>

      {/* Order Notes */}
      <section className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-150">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-151">
          Notas Adicionales
        </h3>
        <Textarea placeholder="¿Algo más que debamos saber sobre tu pedido o entrega?" value={formData.notes} onChange={e => handleChange('notes', e.target.value)} data-id="element-152" />
      </section>

      <Button type="submit" size="lg" fullWidth isLoading={isLoading} className="text-lg shadow-xl" data-id="element-153">
        Confirmar Pedido
      </Button>
    </form>;
}